import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Save, SendHorizonal, Loader2 } from "lucide-react";

const SETTING_KEYS = [
  "resend_api_key",
  "email_from_address",
  "email_from_name",
  "email_admin_address",
] as const;

type SettingKey = (typeof SETTING_KEYS)[number];

export function EmailSettingsSection() {
  const [values, setValues] = useState<Record<SettingKey, string>>({
    resend_api_key: "",
    email_from_address: "",
    email_from_name: "",
    email_admin_address: "",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("settings")
        .select("key, value")
        .in("key", [...SETTING_KEYS]);
      if (data) {
        const next = { ...values };
        data.forEach((r) => {
          if (SETTING_KEYS.includes(r.key as SettingKey)) {
            next[r.key as SettingKey] = r.value;
          }
        });
        setValues(next);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const key of SETTING_KEYS) {
        const { data: existing } = await supabase
          .from("settings")
          .select("id")
          .eq("key", key)
          .maybeSingle();

        if (existing) {
          const { error } = await supabase
            .from("settings")
            .update({ value: values[key] })
            .eq("id", existing.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("settings")
            .insert({ key, value: values[key] });
          if (error) throw error;
        }
      }
      toast({ title: "Guardado", description: "Configuración de correo actualizada." });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!values.resend_api_key || !values.email_from_address || !values.email_admin_address) {
      toast({ title: "Faltan datos", description: "Completa al menos la API Key, correo remitente y correo admin.", variant: "destructive" });
      return;
    }
    setTesting(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-test-email", {
        body: {
          to: values.email_admin_address,
          fromEmail: values.email_from_address,
          fromName: values.email_from_name || "World Transfers Trips",
          apiKey: values.resend_api_key,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: "Enviado", description: "Correo de prueba enviado correctamente." });
    } catch (e: any) {
      toast({ title: "Error al enviar", description: e.message, variant: "destructive" });
    } finally {
      setTesting(false);
    }
  };

  const field = (key: SettingKey, label: string, placeholder: string, type = "text") => (
    <div className="space-y-2">
      <Label htmlFor={key}>{label}</Label>
      <Input
        id={key}
        type={type}
        placeholder={placeholder}
        value={values[key]}
        onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
      />
    </div>
  );

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Configuración de Correo</h2>
        <p className="text-muted-foreground text-sm mt-1">Configura el servicio de envío de correos con Resend</p>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" /> Credenciales Resend
          </CardTitle>
          <CardDescription>Los datos se almacenan de forma segura en la base de datos.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {field("resend_api_key", "API Key de Resend", "re_xxxxxxxxxx", "password")}
          {field("email_from_address", "Correo Remitente", "noreply@tudominio.com")}
          {field("email_from_name", "Nombre Remitente", "World Transfers Trips")}
          {field("email_admin_address", "Correo Administrador", "admin@tudominio.com")}

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar
            </Button>
            <Button variant="outline" onClick={handleTest} disabled={testing}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizonal className="h-4 w-4" />}
              Enviar Correo de Prueba
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
